/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * The cold storage endpoints. `/fresh/...` and `/image` paths pass through the client's
 * route rewriting untouched.
 */

import { DateTime } from 'luxon'

import type { ApiService } from '@/types/dashboard-types'

import {
  type DoorEvent,
  FLOOR_PLAN_USAGE,
  type HistoryPoint,
  type ImageMeta,
  type InStoreAsset,
  type Marker,
  type Threshold,
} from './types'

const ORIGIN = 'coldStorage'

const baseUrl = () =>
  `${window.VITE_MEGALODON_SERVER_URL || import.meta.env.VITE_MEGALODON_SERVER_URL || 'http://localhost:3000'}`

/** `statePath` is the screen's: the cold storage state, or the blast freezer one. */
export const fetchInStoreState = (api: ApiService, statePath: string, companyId: number): Promise<InStoreAsset[]> =>
  api
    .simpleGet<{ data: InStoreAsset[] }>({ path: `${statePath}?companyId=${companyId}`, origin: ORIGIN })
    .then((r) => r.data)

export const fetchImages = (api: ApiService, companyId: number): Promise<ImageMeta[]> =>
  api.simpleGet<{ data: ImageMeta[] }>({ path: `/image?companyId=${companyId}`, origin: ORIGIN }).then((r) => r.data)

/** Add an image to the company's library, shown by no location yet. */
export const uploadImage = (api: ApiService, companyId: number, name: string, file: File): Promise<ImageMeta> =>
  api
    .simplePostBinary<{ data: ImageMeta }>({
      path: `/image?companyId=${companyId}&name=${encodeURIComponent(name)}`,
      body: file,
      contentType: file.type,
      origin: ORIGIN,
    })
    .then((r) => r.data)

export const renameImage = (api: ApiService, imageId: number, name: string): Promise<ImageMeta> =>
  api
    .simplePut<{ data: ImageMeta }, { name: string }>({ path: `/image/${imageId}`, body: { name }, origin: ORIGIN })
    .then((r) => r.data)

/** Delete an image and every link to it. */
export const deleteImage = (api: ApiService, imageId: number): Promise<void> =>
  api.simpleRemove<{ data: { id: number } }>({ path: `/image/${imageId}`, origin: ORIGIN }).then(() => undefined)

/** Stop a location showing a floor plan; the image stays in the library. */
export const unlinkFloorPlan = (api: ApiService, assetId: number): Promise<void> =>
  api
    .simpleRemove<{ data: unknown }>({ path: `/image/asset/${assetId}/${FLOOR_PLAN_USAGE}`, origin: ORIGIN })
    .then(() => undefined)

/** Make an image the company already has the floor plan of a location. */
export const linkFloorPlan = (api: ApiService, assetId: number, imageId: number): Promise<ImageMeta> =>
  api
    .simplePut<{ data: ImageMeta }, { imageId: number }>({
      path: `/image/asset/${assetId}/${FLOOR_PLAN_USAGE}`,
      body: { imageId },
      origin: ORIGIN,
    })
    .then((r) => r.data)

/**
 * A URL an `<img src>` can load without a bearer header: the API signs a short-lived token
 * for exactly this path. The signed and fetched URLs must match byte for byte.
 */
export const fetchImageSrc = async (api: ApiService, imageId: number): Promise<string> => {
  const url = `/image/${imageId}`
  const token = await api.simpleGet<string>({
    path: `${baseUrl()}/auth/downloadToken?url=${encodeURIComponent(url)}`,
    origin: ORIGIN,
  })
  return `${baseUrl()}${url}?downloadToken=${token}`
}

export const putLayout = (api: ApiService, assetId: number, markers: Marker[]): Promise<Marker[]> =>
  api
    .simplePut<{ data: { markers: Marker[] } }, { markers: Marker[] }>({
      path: `/fresh/in-store/asset/${assetId}/layout`,
      body: { markers },
      origin: ORIGIN,
    })
    .then((r) => r.data.markers)

export const putLocationRange = (api: ApiService, assetId: number, range: Threshold): Promise<Threshold> =>
  api
    .simplePut<{ data: Threshold & { assetId: number } }, Threshold>({
      path: `/fresh/in-store/asset/${assetId}/threshold`,
      body: range,
      origin: ORIGIN,
    })
    .then((r) => ({ lowCelsius: r.data.lowCelsius, highCelsius: r.data.highCelsius }))

const historyQuery = (from: DateTime, to: DateTime) =>
  new URLSearchParams({ from: from.toUTC().toISO() ?? '', to: to.toUTC().toISO() ?? '' }).toString()

export const fetchTemperatureHistory = (
  api: ApiService,
  monitoringPointId: number,
  from: DateTime,
  to: DateTime
): Promise<HistoryPoint[]> =>
  api
    .simpleGet<{ data: HistoryPoint[] }>({
      path: `/fresh/sensor/temperature-history/${monitoringPointId}?${historyQuery(from, to)}`,
      origin: ORIGIN,
    })
    .then((r) => r.data)

export const fetchHumidityHistory = (
  api: ApiService,
  monitoringPointId: number,
  from: DateTime,
  to: DateTime
): Promise<HistoryPoint[]> =>
  api
    .simpleGet<{ data: HistoryPoint[] }>({
      path: `/fresh/sensor/humidity-history/${monitoringPointId}?${historyQuery(from, to)}`,
      origin: ORIGIN,
    })
    .then((r) => r.data)

/**
 * One door's open/close events from `from` until now, newest open first; a door still
 * open has no closedDate. Served by the door activities insight's endpoint.
 */
export const fetchDoorHistory = (
  api: ApiService,
  companyId: number,
  assetId: number,
  monitoringPointId: number,
  from: DateTime
): Promise<DoorEvent[]> =>
  api
    .simpleGet<{ data: DoorEvent[] }>({
      path: `/fresh/insights/asset/door-activities?${new URLSearchParams({
        companyId: String(companyId),
        assetId: String(assetId),
        monitoringPointId: String(monitoringPointId),
        date: 'custom',
        from: from.toUTC().toISO() ?? '',
      }).toString()}`,
      origin: ORIGIN,
    })
    .then((r) => r.data)
